import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, CircularProgress, Grid, Typography } from '@mui/material'

import { AccountCard, BaseLayout, CustomAlert } from 'components'
import { useMemberData } from 'hooks/useMemberData'
import { useCheckAndUpdateOktaUser } from 'hooks/useCheckAndUpdateOktaUser'
import { useNavigate } from 'react-router'
import { routes } from 'config'

function MultiLoginSelectAccount() {
  const [errorAlertMessage, setErrorAlertMessage] = useState('')
  const navigate = useNavigate()

  const { data, loading, error } = useMemberData()
  const {
    error: errorUpdateAccount,
    execute: executeUpdateAccount,
    loading: loadingUpdateAccount,
    success: successUpdateAccount,
  } = useCheckAndUpdateOktaUser()

  const handleSelectAccount = useCallback(
    async (memberId: string) => {
      await executeUpdateAccount(memberId)
    },
    [executeUpdateAccount],
  )

  const isLoading = useMemo(
    () => (!error && loading) || (!errorUpdateAccount && loadingUpdateAccount),
    [error, errorUpdateAccount, loading, loadingUpdateAccount],
  )

  useEffect(() => {
    if (successUpdateAccount) {
      navigate(routes.multiLoginSwitchAccountLogout)
    }
  }, [navigate, successUpdateAccount])

  useEffect(() => {
    if (error || errorUpdateAccount) {
      setErrorAlertMessage('Ops, There is an Error!')
    }
  }, [error, errorUpdateAccount])

  return (
    <BaseLayout maxWidth="90em">
      <Box>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h1">Select an Account to Access</Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Your Multi Login is linked to multiple offices. Choose which account
            you&apos;d like to view or manage.
          </Typography>
        </Box>
        {errorAlertMessage && (
          <Box sx={{ mb: 3 }}>
            <CustomAlert severity="error">{errorAlertMessage}</CustomAlert>
          </Box>
        )}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <CircularProgress size={80} />
          </Box>
        )}
        {data && !loading && !error && !isLoading && (
          <Grid container spacing={3}>
            {data.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.memberMlsId}>
                <AccountCard member={item} onSelect={handleSelectAccount} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </BaseLayout>
  )
}

export default MultiLoginSelectAccount
